import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AuthUser, ResumeScore } from "@/types";

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setAuth: (user: AuthUser, token: string, refreshToken: string) => void;
  setToken: (token: string) => void;
  setUser: (u: Partial<AuthUser>) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      setAuth: (user, token, refreshToken) =>
        set({ user, token, refreshToken, isAuthenticated: true }),
      setToken: (token) => set({ token }),
      setUser: (partial) =>
        set((s) => ({ user: s.user ? { ...s.user, ...partial } : null })),
      logout: () =>
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: "iqautoapply-auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        user: s.user,
        token: s.token,
        refreshToken: s.refreshToken,
        isAuthenticated: s.isAuthenticated,
      }),
    },
  ),
);

interface UIState {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
  toggleSidebar: () => void;
}

export const useUIStore = create<UIState>()((set) => ({
  sidebarCollapsed: false,
  setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
}));

// ─── Job Profile Store ────────────────────────────────────────────────────────
interface JobProfileData {
  _id?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  location?: string;
  skills?: string[];
  negativeKeywords?: string[];
  experiences?: Array<{
    _id?: string;
    company: string;
    position: string;
    startDate: string;
    endDate: string;
    description: string;
  }>;
  resumeUrl?: string;
  coverLetterUrl?: string;
  [key: string]: any;
}

interface JobProfileState {
  profile: JobProfileData | null;
  isLoading: boolean;
  setProfile: (profile: JobProfileData) => void;
  updateProfile: (partial: Partial<JobProfileData>) => void;
  clearProfile: () => void;
}

export const useJobProfileStore = create<JobProfileState>()((set) => ({
  profile: null,
  isLoading: false,
  setProfile: (profile) => set({ profile }),
  updateProfile: (partial) =>
    set((s) => ({
      profile: s.profile ? { ...s.profile, ...partial } : null,
    })),
  clearProfile: () => set({ profile: null }),
}));

// ─── Jobs Store ───────────────────────────────────────────────────────────────
interface AppliedJobData {
  _id: string;
  jobTitle: string;
  company: string;
  platform: string;
  status: string;
  appliedDate: string;
  resumeScore?: ResumeScore;
  [key: string]: any;
}

interface JobsState {
  jobs: AppliedJobData[];
  pagination: { page: number; limit: number; total: number };
  filters: { platform?: string; status?: string; search?: string };
  selectedJob: AppliedJobData | null;
  setJobs: (jobs: AppliedJobData[]) => void;
  setPagination: (pagination: Partial<JobsState["pagination"]>) => void;
  setFilters: (filters: Partial<JobsState["filters"]>) => void;
  setSelectedJob: (job: AppliedJobData | null) => void;
  updateJob: (jobId: string, partial: Partial<AppliedJobData>) => void;
  clearJobs: () => void;
}

export const useJobsStore = create<JobsState>()((set) => ({
  jobs: [],
  pagination: { page: 1, limit: 20, total: 0 },
  filters: {},
  selectedJob: null,
  setJobs: (jobs) => set({ jobs }),
  setPagination: (pagination) =>
    set((s) => ({ pagination: { ...s.pagination, ...pagination } })),
  setFilters: (filters) =>
    set((s) => ({ filters: { ...s.filters, ...filters } })),
  setSelectedJob: (job) => set({ selectedJob: job }),
  updateJob: (jobId, partial) =>
    set((s) => ({
      jobs: s.jobs.map((j) => (j._id === jobId ? { ...j, ...partial } : j)),
    })),
  clearJobs: () =>
    set({ jobs: [], pagination: { page: 1, limit: 20, total: 0 } }),
}));

// ─── Dashboard Store ──────────────────────────────────────────────────────────
interface DashboardStats {
  totalApplications?: number;
  successRate?: number;
  platformBreakdown?: Record<string, number>;
  recentJobs?: AppliedJobData[];
  [key: string]: any;
}

interface DashboardState {
  stats: DashboardStats | null;
  lastUpdated: number | null;
  setStats: (stats: DashboardStats) => void;
  updateStats: (partial: Partial<DashboardStats>) => void;
  clearStats: () => void;
}

export const useDashboardStore = create<DashboardState>()((set) => ({
  stats: null,
  lastUpdated: null,
  setStats: (stats) => set({ stats, lastUpdated: Date.now() }),
  updateStats: (partial) =>
    set((s) => ({
      stats: s.stats ? { ...s.stats, ...partial } : null,
      lastUpdated: Date.now(),
    })),
  clearStats: () => set({ stats: null, lastUpdated: null }),
}));

// ─── Platform Credentials Store ───────────────────────────────────────────────
interface PlatformCredential {
  platform: string;
  email: string;
  password: string;
  isBcrypt?: boolean;
  configured: boolean;
}

interface PlatformCredentialsState {
  credentials: Record<string, PlatformCredential>;
  isLoading: boolean;
  setCredentials: (credentials: Record<string, PlatformCredential>) => void;
  updateCredential: (platform: string, credential: PlatformCredential) => void;
  removeCredential: (platform: string) => void;
  clearCredentials: () => void;
}

export const usePlatformCredentialsStore = create<PlatformCredentialsState>()(
  persist(
    (set) => ({
      credentials: {},
      isLoading: false,
      setCredentials: (credentials) => set({ credentials }),
      updateCredential: (platform, credential) =>
        set((s) => ({
          credentials: { ...s.credentials, [platform]: credential },
        })),
      removeCredential: (platform) =>
        set((s) => {
          const updated = { ...s.credentials };
          delete updated[platform];
          return { credentials: updated };
        }),
      clearCredentials: () => set({ credentials: {} }),
    }),
    {
      name: "iqautoapply-platform-credentials",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        credentials: s.credentials,
      }),
    },
  ),
);

// ─── Subscription Store ───────────────────────────────────────────────────────
interface Plan {
  _id: string;
  title: string;
  type: string;
  price: number;
  period: string;
  jobsAllowed: string;
  details: string[];
  [key: string]: any;
}

interface SubscriptionState {
  plans: Plan[];
  currentPlan: Plan | null;
  billingPeriod: "Monthly" | "Quarterly";
  setBillingPeriod: (period: "Monthly" | "Quarterly") => void;
  setPlans: (plans: Plan[]) => void;
  setCurrentPlan: (plan: Plan | null) => void;
  clearSubscription: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>()((set) => ({
  plans: [],
  currentPlan: null,
  billingPeriod: "Monthly",
  setBillingPeriod: (period) => set({ billingPeriod: period }),
  setPlans: (plans) => set({ plans }),
  setCurrentPlan: (plan) => set({ currentPlan: plan }),
  clearSubscription: () =>
    set({ plans: [], currentPlan: null, billingPeriod: "Monthly" }),
}));

// ─── Analytics Store ──────────────────────────────────────────────────────────
interface AnalyticsStats {
  totalApplications?: number;
  successRate?: number;
  platformBreakdown?: Record<string, number>;
  chartData?: any[];
  [key: string]: any;
}

interface AnalyticsState {
  stats: AnalyticsStats | null;
  dateRange: string;
  selectedPlatform: string;
  setStats: (stats: AnalyticsStats) => void;
  setDateRange: (range: string) => void;
  setSelectedPlatform: (platform: string) => void;
  clearAnalytics: () => void;
}

export const useAnalyticsStore = create<AnalyticsState>()((set) => ({
  stats: null,
  dateRange: "30",
  selectedPlatform: "all",
  setStats: (stats) => set({ stats }),
  setDateRange: (dateRange) => set({ dateRange }),
  setSelectedPlatform: (selectedPlatform) => set({ selectedPlatform }),
  clearAnalytics: () =>
    set({ stats: null, dateRange: "30", selectedPlatform: "all" }),
}));
